import type { FontStem } from "./fonts";
import { hex, type Rgb, type TextStyle } from "./theme";

// A tiny layout tree that mirrors the subset of the `pdf` package's widgets
// the app's templates use (Column, Row, Expanded, Wrap, Container, Text,
// RichText, SvgImage, Stack/Positioned). Templates build it; renderHtml.tsx
// turns it into DOM for the on-page previews and renderPdf.tsx into
// react-pdf elements for the download, so both always share one layout.

export type Edge = [top: number, right: number, bottom: number, left: number];

export type Border = { width: number; color: string; dashed?: boolean };

export type BoxStyle = {
  /** Default "column", like pw.Column. */
  dir?: "row" | "column";
  wrap?: boolean;
  /** Row with MainAxisSize.min (sizes to its children instead of filling). */
  min?: boolean;
  /** Expands to fill its column, like an empty Container or pw.Center. */
  fill?: boolean;
  /** Main-axis gap (Wrap spacing) and cross-axis gap (Wrap runSpacing). */
  gap?: number;
  runGap?: number;
  /** align-items. Defaults: column → "start", row → "center" (pw.Row). */
  align?: "start" | "center" | "end" | "stretch";
  justify?: "start" | "center" | "end" | "between";
  alignSelf?: "start" | "center" | "end" | "stretch";
  /** Flex grow (pw.Expanded flex). A grown child also gets basis 0 / min-width 0. */
  grow?: number;
  /** pw.Flexible: may shrink below its content width. */
  shrinkable?: boolean;
  width?: number | "100%";
  height?: number;
  pad?: Edge;
  margin?: Edge;
  bg?: string;
  radius?: number;
  border?: Border;
  borderLeft?: Border;
  borderBottom?: Border;
  position?: "relative" | "absolute";
  top?: number;
  left?: number;
  right?: number;
  bottom?: number;
};

export type TextRun = {
  text: string;
  font: FontStem;
  size: number;
  color: string;
  letterSpacing: number;
  /** Link target (a clickable annotation in the PDF; never changes layout). */
  href?: string;
};

export type TextNode = {
  kind: "text";
  runs: TextRun[];
  /** Extra space between wrapped lines (from the first run's style). */
  lineSpacing: number;
  align?: "left" | "right" | "center";
  /** Grow / shrink when placed in a row (Expanded / Flexible). */
  grow?: number;
  shrinkable?: boolean;
  /** Character offsets where a new line starts, measured from the HTML preview (see lineBreaks.ts). */
  breaks?: number[];
  /** Which part of the resume drew this block (preview interaction only; never affects layout). */
  tag?: string;
};

export type BoxNode = {
  kind: "box";
  style: BoxStyle;
  children: Node[];
  /** Never split across PDF pages (the app's keep()). */
  keep?: boolean;
  /** Which part of the resume drew this block (preview interaction only; never affects layout). */
  tag?: string;
};

export type GlyphNode = { kind: "glyph"; path: string; color: string; size: number };

/** A square image (the profile photo), pre-cropped so it needs no object-fit. */
export type ImageNode = { kind: "image"; src: string; size: number; radius: number };

export type Node = TextNode | BoxNode | GlyphNode | ImageNode;

export type PageSize = { width: number; height: number };

export type Band = { side: "left" | "right"; width: number; color: string };

/**
 * Two independently flowing columns (the app's pw.Partitions): each column
 * paginates on its own and continues on the next page; one side has a fixed
 * width, the other takes the rest.
 */
export type Columns = { left: Node[]; right: Node[]; leftWidth?: number; rightWidth?: number };

/** One page design: frame, optional full-height side bands, and content. */
export type PageSpec = {
  size: PageSize;
  /** Content insets; `pad[2]` doubles as the footer height. */
  pad: Edge;
  /** Top inset on page 1 only (full-bleed banners use 0). */
  firstTop?: number;
  /** Full-height colour bands behind every page (sidebars). */
  bands: Band[];
  footer: { right: number; bottom: number; color: string; font: FontStem };
  /** Shown in the multi-page footer ("Name · 2 / 3"). */
  name: string;
  /** The flowing content of a single-column design. */
  body: Node[];
  /** Two-column designs flow these instead of [body]. */
  columns?: Columns;
};

// ---- builders ---------------------------------------------------------------

/** A builder child; falsy entries (from `cond && node`) are dropped. */
export type Child = Node | null | false | undefined | "" | 0;

export function box(style: BoxStyle, children: Child[] = []): BoxNode {
  return { kind: "box", style, children: children.filter(Boolean) as Node[] };
}

export function col(children: Child[], style: BoxStyle = {}): BoxNode {
  return box({ dir: "column", ...style }, children);
}

export function row(children: Child[], style: BoxStyle = {}): BoxNode {
  return box({ dir: "row", align: "center", ...style }, children);
}

/** pw.Row(mainAxisSize: MainAxisSize.min) */
export function rowMin(children: Child[], style: BoxStyle = {}): BoxNode {
  return row(children, { min: true, ...style });
}

/** pw.Wrap (items centred on each run unless [cross] says otherwise). */
export function wrapBox(
  children: Child[],
  o: { spacing?: number; runSpacing?: number; center?: boolean; cross?: "start" | "center" } = {}
): BoxNode {
  return box(
    {
      dir: "row",
      wrap: true,
      gap: o.spacing ?? 0,
      runGap: o.runSpacing ?? 0,
      align: o.cross ?? "center",
      justify: o.center ? "center" : "start",
    },
    children
  );
}

/** pw.Center: fills its column and centres the child. */
export function center(child: Node): BoxNode {
  return box({ align: "center", fill: true }, [child]);
}

/**
 * Whether a node fills the width of a column it sits in, the way Flutter-style
 * constraints work: a Row (MainAxisSize.max), an empty Container or a Center
 * takes all available width, and a Column containing one grows to match. CSS
 * and Yoga instead shrink-wrap column children, so the renderers stretch
 * every child for which this returns true.
 */
export function fills(n: Node): boolean {
  if (n.kind !== "box") return false;
  const s = n.style;
  if (s.width !== undefined) return s.width === "100%";
  if (s.fill) return true;
  if (s.dir === "row") return !s.wrap && !s.min;
  return n.children.some(fills);
}

/** pw.Expanded */
export function expanded(child: Node, flex = 1): Node {
  if (child.kind === "text") return { ...child, grow: flex };
  if (child.kind === "box" && !child.keep && child.style.grow === undefined && !child.style.width) {
    return { ...child, style: { ...child.style, grow: flex } };
  }
  return box({ grow: flex }, [child]);
}

/** pw.Flexible (loose fit) */
export function flexible(child: Node): Node {
  if (child.kind === "text") return { ...child, shrinkable: true };
  return box({ shrinkable: true }, [child]);
}

export function sized(width: number | undefined, height: number | undefined, child?: Node | null): BoxNode {
  return box({ width, height }, [child]);
}

export function vspace(height: number): BoxNode {
  return box({ height }, []);
}

export function hspace(width: number): BoxNode {
  return box({ width }, []);
}

export function padding(pad: Partial<{ top: number; right: number; bottom: number; left: number }>, child: Node): BoxNode {
  return box({ pad: [pad.top ?? 0, pad.right ?? 0, pad.bottom ?? 0, pad.left ?? 0] }, [child]);
}

export function padH(child: Node, left: number, right: number): BoxNode {
  return box({ pad: [0, right, 0, left] }, [child]);
}

/** A filled rectangle (markers, or a rule when given no width inside Expanded). */
export function rect(opts: { width?: number; height: number; color: Rgb; radius?: number }): BoxNode {
  return box({ width: opts.width, height: opts.height, bg: hex(opts.color), radius: opts.radius });
}

/** A horizontal rule spanning its column (an empty pw.Container with a height). */
export function hrule(height: number, color: Rgb): BoxNode {
  return box({ height, bg: hex(color), fill: true });
}

/** Groups blocks so PDF pagination never splits them (the app's keep()). */
export function keep(children: Child[], stretch = false): BoxNode {
  return { ...col(children, stretch ? { align: "stretch" } : {}), keep: true };
}

export function run(text: string, style: TextStyle, href?: string | null): TextRun {
  const r: TextRun = { text, font: style.font, size: style.size, color: hex(style.color), letterSpacing: style.letterSpacing };
  if (href) r.href = href;
  return r;
}

export function text(value: string, style: TextStyle, align?: TextNode["align"], href?: string | null): TextNode {
  return { kind: "text", runs: [run(value, style, href)], lineSpacing: style.lineSpacing, align };
}

/** pw.UrlLink around a text: the same text, clickable in the PDF. */
export function linkText(value: string, style: TextStyle, href: string | null | undefined, align?: TextNode["align"]): TextNode {
  return text(value, style, align, href);
}

/** pw.RichText; line spacing follows the first span, as the `pdf` package does. */
export function rich(
  spans: (readonly [string, TextStyle] | readonly [string, TextStyle, string | null | undefined] | null | false)[],
  align?: TextNode["align"]
): TextNode {
  const list = spans.filter(Boolean) as (readonly [string, TextStyle, (string | null | undefined)?])[];
  return {
    kind: "text",
    runs: list.map(([value, style, href]) => run(value, style, href)),
    lineSpacing: list.length ? list[0][1].lineSpacing : 0,
    align,
  };
}

export function glyph(path: string, color: Rgb, size: number): GlyphNode {
  return { kind: "glyph", path, color: hex(color), size };
}

export function image(src: string, size: number, radius: number): ImageNode {
  return { kind: "image", src, size, radius };
}

/** An absolutely positioned decoration drawn inside a box (offsets in pt). */
export type Overlay = {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
  width?: number;
  height?: number;
  bg?: string;
  /** Uniform stroke on all sides, or a top stroke (dashed rules). */
  stroke?: { sides: "all" | "top"; width: number; color: string; dashed?: boolean };
  radius?: number;
};

/**
 * Borders the way the `pdf` package paints them: stroked centred on the box
 * edge (half outside) and ignored by layout. CSS and Yoga borders sit inside
 * the box and push content in, so both renderers draw these overlays instead.
 */
export function borderOverlays(s: BoxStyle): Overlay[] {
  const out: Overlay[] = [];
  if (s.border) {
    const w = s.border.width;
    out.push({
      top: -w / 2,
      right: -w / 2,
      bottom: -w / 2,
      left: -w / 2,
      stroke: { sides: "all", width: w, color: s.border.color, dashed: s.border.dashed },
      radius: s.radius ? s.radius + w / 2 : undefined,
    });
  }
  if (s.borderLeft) {
    const w = s.borderLeft.width;
    out.push({ top: 0, bottom: 0, left: -w / 2, width: w, bg: s.borderLeft.color });
  }
  if (s.borderBottom) {
    const w = s.borderBottom.width;
    out.push({
      left: 0,
      right: 0,
      bottom: -w / 2,
      height: w,
      stroke: { sides: "top", width: w, color: s.borderBottom.color, dashed: s.borderBottom.dashed },
    });
  }
  return out;
}

/** Empty space only: spacers, or plain boxes holding nothing but spacers. */
function blank(n: Node): boolean {
  if (n.kind !== "box") return false;
  const s = n.style;
  if (s.bg || s.border || s.borderLeft || s.borderBottom || s.width !== undefined) return false;
  return n.children.every(blank);
}

/**
 * Drops trailing section gaps at the end of the document (recursing into the
 * last flowing block), so a gap that misses the last page can never push an
 * empty page into the PDF.
 */
export function trimTrailingSpace(nodes: Node[]): Node[] {
  const out = [...nodes];
  while (out.length && blank(out[out.length - 1])) out.pop();
  const last = out[out.length - 1];
  if (last?.kind === "box" && !last.keep && (last.style.dir ?? "column") === "column") {
    const flowing = last.children.filter((c) => c.kind !== "box" || c.style.position !== "absolute");
    const fixed = last.children.filter((c) => !flowing.includes(c));
    out[out.length - 1] = { ...last, children: [...trimTrailingSpace(flowing), ...fixed] };
  }
  return out;
}

/** Walks a tree and collects every font stem it uses. */
export function collectFonts(nodes: Node[], into = new Set<FontStem>()): Set<FontStem> {
  for (const n of nodes) {
    if (n.kind === "text") for (const r of n.runs) into.add(r.font);
    else if (n.kind === "box") collectFonts(n.children, into);
  }
  return into;
}
